const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
const DAY_MS = 86_400_000;

function parseIsoDate(value) {
  if (typeof value !== "string" || !ISO_DATE.test(value)) {
    throw new Error("Ongeldige ISO-datum.");
  }
  const date = new Date(`${value}T00:00:00.000Z`);
  if (Number.isNaN(date.getTime()) || date.toISOString().slice(0, 10) !== value) {
    throw new Error("Ongeldige ISO-datum.");
  }
  return date;
}

export function toIsoDateLocal(date = new Date()) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

export function addIsoDays(value, days) {
  if (!Number.isInteger(days)) throw new Error("days moet een geheel getal zijn.");
  const date = parseIsoDate(value);
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
}

export function mondayForIsoDate(value) {
  const date = parseIsoDate(value);
  const weekday = date.getUTCDay();
  const delta = weekday === 0 ? -6 : 1 - weekday;
  return addIsoDays(value, delta);
}

export function workWeekForIsoDate(value) {
  const date = parseIsoDate(value);
  const thursday = new Date(date);
  const weekday = thursday.getUTCDay() || 7;
  thursday.setUTCDate(thursday.getUTCDate() + 4 - weekday);

  const weekYear = thursday.getUTCFullYear();
  const yearStart = new Date(Date.UTC(weekYear, 0, 1));
  const weekNumber = Math.ceil(((thursday.getTime() - yearStart.getTime()) / DAY_MS + 1) / 7);

  return {
    weekYear,
    weekNumber,
    label: `WW${String(weekNumber).padStart(2, "0")}`,
  };
}

export function daysBetweenInclusive(startDate, endDate) {
  const start = parseIsoDate(startDate);
  const end = parseIsoDate(endDate);
  const delta = Math.round((end.getTime() - start.getTime()) / DAY_MS) + 1;
  if (delta <= 0) throw new Error("Einddatum ligt voor startdatum.");
  return delta;
}

export function formatPlanningDate(value, options = {}) {
  if (!value) return "Niet gepland";
  const date = parseIsoDate(value);
  return new Intl.DateTimeFormat("nl-NL", {
    day: "numeric",
    month: options.short ? "short" : "long",
    ...(options.withYear === false ? {} : { year: "numeric" }),
    timeZone: "UTC",
  }).format(date);
}

export function formatDateRange(startDate, dueDate) {
  if (!startDate && !dueDate) return "Niet gepland";
  if (startDate && dueDate && startDate === dueDate) return formatPlanningDate(startDate);
  if (!startDate) return `t/m ${formatPlanningDate(dueDate)}`;
  if (!dueDate) return `vanaf ${formatPlanningDate(startDate)}`;
  return `${formatPlanningDate(startDate, { short: true })} – ${
    formatPlanningDate(dueDate, { short: true })
  }`;
}

function comparableStart(item) {
  return item.startDate ?? item.dueDate ?? "9999-12-31";
}

function sortItems(a, b) {
  return comparableStart(a).localeCompare(comparableStart(b)) || a.id - b.id;
}

export function orderWorkPackages(workPackages) {
  const items = Array.isArray(workPackages) ? workPackages : [];
  const byId = new Map(items.map((item) => [item.id, item]));
  const children = new Map();
  for (const item of items) {
    const parentId = byId.has(item.parentId) ? item.parentId : null;
    const list = children.get(parentId) ?? [];
    list.push(item);
    children.set(parentId, list);
  }
  for (const list of children.values()) list.sort(sortItems);

  const result = [];
  const seen = new Set();
  const visit = (item, depth) => {
    if (seen.has(item.id)) return;
    seen.add(item.id);
    result.push({ item, depth });
    for (const child of children.get(item.id) ?? []) visit(child, depth + 1);
  };
  for (const root of children.get(null) ?? []) visit(root, 0);
  for (const item of [...items].sort(sortItems)) visit(item, 0);
  return result;
}

function normalizeItemRange(item) {
  const startDate = item.startDate ?? item.dueDate;
  const dueDate = item.dueDate ?? item.startDate;
  if (!startDate || !dueDate) return null;
  return { startDate, dueDate };
}

function buildMonthSegments(rangeStart, rangeEnd) {
  const totalDays = daysBetweenInclusive(rangeStart, rangeEnd);
  const segments = [];
  let cursor = rangeStart;
  while (cursor <= rangeEnd) {
    const date = parseIsoDate(cursor);
    const nextMonth = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + 1, 1));
    const nextMonthIso = nextMonth.toISOString().slice(0, 10);
    const candidateEnd = addIsoDays(nextMonthIso, -1);
    const segmentEnd = candidateEnd < rangeEnd ? candidateEnd : rangeEnd;
    const offsetDays = daysBetweenInclusive(rangeStart, cursor) - 1;
    const durationDays = daysBetweenInclusive(cursor, segmentEnd);
    segments.push({
      label: new Intl.DateTimeFormat("nl-NL", {
        month: "short",
        year: "numeric",
        timeZone: "UTC",
      }).format(date),
      offsetDays,
      durationDays,
      offsetPercent: (offsetDays / totalDays) * 100,
      widthPercent: (durationDays / totalDays) * 100,
    });
    cursor = nextMonthIso;
  }
  return segments;
}

function buildWeekSegments(rangeStart, rangeEnd) {
  const totalDays = daysBetweenInclusive(rangeStart, rangeEnd);
  const segments = [];
  let weekStart = mondayForIsoDate(rangeStart);

  while (weekStart <= rangeEnd) {
    const segmentStart = weekStart < rangeStart ? rangeStart : weekStart;
    const naturalWeekEnd = addIsoDays(weekStart, 6);
    const segmentEnd = naturalWeekEnd > rangeEnd ? rangeEnd : naturalWeekEnd;
    const offsetDays = daysBetweenInclusive(rangeStart, segmentStart) - 1;
    const durationDays = daysBetweenInclusive(segmentStart, segmentEnd);
    const workWeek = workWeekForIsoDate(weekStart);

    segments.push({
      ...workWeek,
      startDate: weekStart,
      endDate: naturalWeekEnd,
      offsetDays,
      durationDays,
      offsetPercent: (offsetDays / totalDays) * 100,
      widthPercent: (durationDays / totalDays) * 100,
    });

    weekStart = addIsoDays(weekStart, 7);
  }

  return segments;
}

export function buildGanttModel(plan) {
  const ordered = orderWorkPackages(plan?.workPackages ?? []);
  const scheduled = ordered.filter(({ item }) => normalizeItemRange(item));
  const unscheduled = ordered.filter(({ item }) => !normalizeItemRange(item));

  if (scheduled.length === 0) {
    return {
      rangeStart: null,
      rangeEnd: null,
      totalDays: 0,
      dayWidth: 12,
      timelineWidth: 0,
      months: [],
      weeks: [],
      rows: [],
      unscheduled,
    };
  }

  const ranges = scheduled.map(({ item }) => normalizeItemRange(item));
  const rangeStart = ranges.map((range) => range.startDate).sort()[0];
  const sortedEndDates = ranges.map((range) => range.dueDate).sort();
  const rangeEnd = sortedEndDates[sortedEndDates.length - 1];
  const totalDays = daysBetweenInclusive(rangeStart, rangeEnd);
  const dayWidth = totalDays <= 120 ? 14 : totalDays <= 365 ? 8 : 4;
  const timelineWidth = Math.min(12_000, Math.max(720, totalDays * dayWidth));
  const effectiveDayWidth = timelineWidth / totalDays;

  const rows = scheduled.map(({ item, depth }) => {
    const range = normalizeItemRange(item);
    const offsetDays = daysBetweenInclusive(rangeStart, range.startDate) - 1;
    const durationDays = daysBetweenInclusive(range.startDate, range.dueDate);
    return {
      item,
      depth,
      offsetDays,
      durationDays,
      left: (offsetDays + (item.type?.milestone ? 0.5 : 0)) * effectiveDayWidth,
      width: Math.max(item.type?.milestone ? 14 : 3, durationDays * effectiveDayWidth),
    };
  });

  return {
    rangeStart,
    rangeEnd,
    totalDays,
    dayWidth: effectiveDayWidth,
    timelineWidth,
    months: buildMonthSegments(rangeStart, rangeEnd),
    weeks: buildWeekSegments(rangeStart, rangeEnd),
    rows,
    unscheduled,
  };
}

export function overlapsWindow(item, startDate, endDate) {
  const range = normalizeItemRange(item);
  if (!range) return false;
  return range.startDate <= endDate && range.dueDate >= startDate;
}

export function buildLookaheadBuckets(readModel, numberOfWeeks = 12) {
  if (!Number.isInteger(numberOfWeeks) || numberOfWeeks <= 0 || numberOfWeeks > 52) {
    throw new Error("numberOfWeeks moet tussen 1 en 52 liggen.");
  }
  const startDate = mondayForIsoDate(readModel.startDate);
  const items = Array.isArray(readModel.workPackages) ? readModel.workPackages : [];
  return Array.from({ length: numberOfWeeks }, (_, index) => {
    const weekStart = addIsoDays(startDate, index * 7);
    const weekEnd = addIsoDays(weekStart, 6);
    const workWeek = workWeekForIsoDate(weekStart);
    return {
      index: index + 1,
      ...workWeek,
      startDate: weekStart,
      endDate: weekEnd,
      items: items.filter((item) => overlapsWindow(item, weekStart, weekEnd)).sort(sortItems),
    };
  });
}

export function rollingWaveDetailState(item, workPackages, horizonStart) {
  const items = Array.isArray(workPackages) ? workPackages : [];
  const isTopLevelPhase = item?.parentId == null && item?.type?.milestone !== true;
  if (!isTopLevelPhase) {
    return { needsDetail: false, urgency: "none", reason: null };
  }

  const horizonMonday = mondayForIsoDate(horizonStart);
  const nearTermEnd = addIsoDays(horizonMonday, 13);
  const horizonEnd = addIsoDays(horizonMonday, 83);
  const range = normalizeItemRange(item);

  if (!range) {
    return {
      needsDetail: true,
      urgency: "dates",
      reason: "Top-level fase heeft nog geen bekende planningdatums.",
    };
  }

  if (!overlapsWindow(item, horizonMonday, horizonEnd)) {
    return { needsDetail: false, urgency: "none", reason: null };
  }

  const children = items.filter((candidate) => candidate.parentId === item.id);
  const childrenInHorizon = children.filter((candidate) =>
    overlapsWindow(candidate, horizonMonday, horizonEnd)
  );
  const isNearTerm = overlapsWindow(item, horizonMonday, nearTermEnd);

  if (childrenInHorizon.length === 0) {
    return {
      needsDetail: true,
      urgency: isNearTerm ? "near_term" : "lookahead",
      reason: isNearTerm
        ? "Fase valt binnen de komende 2 weken maar heeft nog geen ingeplande child-werkpakketten."
        : "Fase valt binnen de komende 12 weken maar heeft nog geen ingeplande child-werkpakketten.",
    };
  }

  if (
    isNearTerm &&
    !children.some((candidate) => overlapsWindow(candidate, horizonMonday, nearTermEnd))
  ) {
    return {
      needsDetail: true,
      urgency: "near_term",
      reason:
        "Fase valt binnen de komende 2 weken maar heeft daar nog geen uitvoerbaar child-werkpakket.",
    };
  }

  return { needsDetail: false, urgency: "none", reason: null };
}

export function buildRollingWaveSummary(workPackages, horizonStart) {
  const items = Array.isArray(workPackages) ? workPackages : [];
  const horizonMonday = mondayForIsoDate(horizonStart);
  const horizonEnd = addIsoDays(horizonMonday, 83);
  const phases = items.filter((item) => item.parentId == null && item.type?.milestone !== true);
  const states = phases.map((item) => ({
    item,
    state: rollingWaveDetailState(item, items, horizonMonday),
  }));
  return {
    topLevelPhaseCount: phases.length,
    horizonPhaseCount:
      phases.filter((item) => overlapsWindow(item, horizonMonday, horizonEnd)).length,
    needsDetailCount: states.filter(({ state }) => state.needsDetail).length,
    nearTermNeedsDetailCount: states.filter(({ state }) => state.urgency === "near_term").length,
    items: states,
  };
}

export function dependencyLabels(workPackageId, dependencies) {
  const items = Array.isArray(dependencies) ? dependencies : [];
  const predecessors = items
    .filter((item) => item.toWorkPackageId === workPackageId && item.type === "precedes")
    .map((item) => item.fromWorkPackageId)
    .sort((a, b) => a - b);
  const successors = items
    .filter((item) => item.fromWorkPackageId === workPackageId && item.type === "precedes")
    .map((item) => item.toWorkPackageId)
    .sort((a, b) => a - b);
  return { predecessors, successors };
}
